import { WidgetCard } from "@/components/widgets";
import { Button, Col, Flex, Input, Row, Tag } from "antd";
import { useState, useEffect } from "react";
import { BCC_KEYWORD_TYPES, IBCCKeyword, INegativeKeywordTarget } from "@/ts/common-types";

interface AddNegativeKeywordsProps {
    handleKeywordSelection: (selectedRows: any[]) => void;
    selectedKeywords?: IBCCKeyword[];
}

interface INegativeKeywordWithId extends INegativeKeywordTarget {
    id: string;
}

const AddNegativeKeywords = ({
    handleKeywordSelection,
    selectedKeywords = [],
}: AddNegativeKeywordsProps) => {

    const [negativeKeywords, setNegativeKeywords] = useState<INegativeKeywordWithId[]>([]);

    const [keyword, setKeyword] = useState<string>("");

    // Initialize with existing negative keywords from selectedKeywords
    useEffect(() => {
        const existingNegativeKeywords = selectedKeywords
            .filter((kw: IBCCKeyword) => kw?.keyword_type === BCC_KEYWORD_TYPES.NEGATIVE)
            .map((kw: IBCCKeyword, index: number) => ({
                id: String(index + 1),
                keyword: kw.keyword || "",
                keyword_type: BCC_KEYWORD_TYPES.NEGATIVE,
            }));

        if (existingNegativeKeywords.length > 0) {
            setNegativeKeywords(existingNegativeKeywords);
        }
    }, []);

    const handleAddKeyword = (keyword: string) => {
        if (!keyword || keyword.trim() === "") return;

        const newKeyword: INegativeKeywordWithId = {
            id: String(negativeKeywords.length + 1),
            keyword: keyword.trim(),
            keyword_type: BCC_KEYWORD_TYPES.NEGATIVE,
        };
        const updatedKeywords = [...negativeKeywords, newKeyword];
        setNegativeKeywords(updatedKeywords);

        const negativeKeywordRows: INegativeKeywordTarget[] = updatedKeywords.map((kw) => ({
            keyword: kw.keyword,
            keyword_type: kw.keyword_type,
        }));

        handleKeywordSelection(negativeKeywordRows);
    };

    const handleTagClose = (id: string) => {
        const updatedKeywords = negativeKeywords.filter(
            (keyword) => keyword.id !== id
        );
        setNegativeKeywords(updatedKeywords);

        // Update the selection with remaining keywords
        const negativeKeywordRows: INegativeKeywordTarget[] = updatedKeywords.map((kw) => ({
            keyword: kw.keyword,
            keyword_type: kw.keyword_type,
        }));
        
        handleKeywordSelection(negativeKeywordRows);
    };
    return (
        <div className="add-negative-keywords-container">
            <WidgetCard bodyPaddingClass="p-0">
                <div className="title-input-container">
                    <Row gutter={16} align="middle">
                        <Col xs={24} sm={24} md={8} lg={6}>
                            <h6>Add Negative Keywords</h6>
                        </Col>
                    </Row>
                    <Row gutter={10} className="input-btn-container">
                        <Col md={8} lg={8}>
                            <Input
                                placeholder="Enter keyword"
                                value={keyword}
                                onChange={(e) => setKeyword(e.target.value)}
                                onPressEnter={() => {
                                    handleAddKeyword(keyword);
                                    setKeyword("");
                                }}
                            />
                        </Col>
                        <Col md={8} lg={6}>
                            <Button
                                type="primary"
                                className="add-negative-keyword-btn"
                                onClick={() => {
                                    handleAddKeyword(keyword);
                                    setKeyword("");
                                }}
                                disabled={!keyword}
                            >
                                Add Keyword
                            </Button>
                        </Col>
                    </Row>
                </div>
                <hr></hr>

                <Row className="tags-container">
                    <Flex>
                        {negativeKeywords.map((keyword) => (
                            <Tag
                                closable={true}
                                onClose={() => handleTagClose(keyword.id)}
                                key={`${keyword.keyword}-${keyword.id}`}
                            >
                                {keyword.keyword}
                            </Tag>
                        ))}
                    </Flex>
                </Row>
            </WidgetCard>
        </div>
    );
};

export default AddNegativeKeywords;
